import { z } from 'zod';
import {
  type WorkbookAggregateInput,
  type WorkbookResultRowsInput,
  BarChartComponentSchema,
  KpiComponentSchema,
  LineChartComponentSchema,
  PieChartComponentSchema,
  RadarChartComponentSchema,
  ScatterChartComponentSchema,
  TableComponentSchema,
  type WorkbookAggregation,
  type ReportFilter,
  reportFiltersFor,
} from '@erp/shared-types';
import type { ReportComponentConfig } from '@/components/report-canvas/types';

/** The charts that plot what Server aggregation can summarize. */
const aggregatedChartSchema = z.discriminatedUnion('type', [
  BarChartComponentSchema,
  KpiComponentSchema,
  LineChartComponentSchema,
  PieChartComponentSchema,
  RadarChartComponentSchema,
  ScatterChartComponentSchema,
]);
const AGGREGATED_TYPES: ReadonlySet<string> = new Set(
  aggregatedChartSchema.options.map((option) => option.shape.type.value),
);

/** Whether a component type can chart a Result past the Client row limit. */
function aggregatesOnServer(type: string): boolean {
  return AGGREGATED_TYPES.has(type);
}

/** A category chart's value summed per category; null until both are bound. */
function sumBy(
  category: string | null,
  value: string | null,
): WorkbookAggregation | null {
  if (!category || !value || category === value) return null;
  return { groupBy: [category], measures: [{ column: value, fn: 'sum' }] };
}

/**
 * The Server aggregation that gives a chart what it plots from every row: a
 * category chart's value summed per category (it sums again in the browser,
 * which leaves each total as it is), a scatter chart's distinct points. Null
 * for a component that isn't bound yet, or whose rows can't be summarized
 * (a vega chart's spec reads rows its own way).
 */
export function componentAggregation(
  component: ReportComponentConfig,
): WorkbookAggregation | null {
  const parsed = aggregatedChartSchema.safeParse(component);
  if (!parsed.success) return null;
  const chart = parsed.data;
  switch (chart.type) {
    case 'bar-chart':
      return barAggregation(chart);
    case 'line-chart': {
      // A line's x axis runs in value order (dates, numbers), not first appearance.
      const summed = sumBy(chart.xAxisKey, chart.yAxisKey);
      return summed && { ...summed, sortGroups: true };
    }
    case 'kpi':
      return kpiAggregation(chart);
    case 'pie-chart':
      return sumBy(chart.nameKey, chart.valueKey);
    case 'radar-chart':
      return sumBy(chart.subjectKey, chart.valueKey);
    case 'scatter-chart':
      return scatterAggregation(chart);
  }
}

function barAggregation(
  chart: z.infer<typeof BarChartComponentSchema>,
): WorkbookAggregation | null {
  const summed = sumBy(chart.xAxisKey, chart.yAxisKey);
  // A Top N by value is the server's top (or bottom) N groups; by label
  // the browser sorts every group it gets and keeps its first N.
  const byValue = chart.sort === 'value-desc' || chart.sort === 'value-asc';
  if (!summed || chart.topN <= 0 || !byValue) return summed;
  return {
    ...summed,
    limit: chart.topN,
    order: chart.sort === 'value-asc' ? 'asc' : 'desc',
  };
}

function kpiAggregation(
  chart: z.infer<typeof KpiComponentSchema>,
): WorkbookAggregation | null {
  if (!chart.valueKey) return null;
  const measures = [{ column: chart.valueKey, fn: chart.fn }];
  const { comparison } = chart;
  if (
    comparison.kind === 'column' &&
    comparison.column &&
    comparison.column !== chart.valueKey
  )
    measures.push({ column: comparison.column, fn: chart.fn });
  return { groupBy: [], measures };
}

function scatterAggregation(
  chart: z.infer<typeof ScatterChartComponentSchema>,
): WorkbookAggregation | null {
  const { xAxisKey, yAxisKey } = chart;
  if (!xAxisKey || !yAxisKey || xAxisKey === yAxisKey) return null;
  return { groupBy: [xAxisKey, yAxisKey], measures: [] };
}

/** How many leading rows a component shows past the Client row limit: a Table's; null for the rest. */
function componentLeadingRows(component: ReportComponentConfig): number | null {
  const parsed = TableComponentSchema.safeParse(component);
  return parsed.success ? parsed.data.maxRows : null;
}

/**
 * What a component asks the server for when its dataset is past the Client
 * row limit: a Table its leading rows, a chart its Server aggregation, and a
 * component that can't be summarized (a vega chart, an unbound chart) nothing.
 * Null while the dataset's rows are in the browser.
 */
export type ServerRowsRequest =
  | { kind: 'leading'; input: WorkbookResultRowsInput & { limit: number } }
  | { kind: 'aggregate'; input: WorkbookAggregateInput }
  | { kind: 'none'; summarizable: boolean };

export function serverRowsRequest(
  workbookId: string,
  source:
    | {
        path: string;
        runId: string | undefined;
        sheet: { id: string };
        columns: readonly { name: string }[];
      }
    | undefined,
  component: ReportComponentConfig,
  filters: readonly ReportFilter[] = [],
): ServerRowsRequest | null {
  if (source?.path !== 'server' || !source.runId) return null;
  // Only the filters on the Worksheet's columns, so an unrelated one doesn't refetch.
  const applying = reportFiltersFor(
    filters,
    source.columns.map((column) => column.name),
  );
  const target = {
    workbookId,
    runId: source.runId,
    sheetId: source.sheet.id,
    ...(applying.length && { filters: applying }),
  };
  const limit = componentLeadingRows(component);
  if (limit !== null) return { kind: 'leading', input: { ...target, limit } };
  const aggregation = componentAggregation(component);
  if (aggregation)
    return { kind: 'aggregate', input: { ...target, aggregation } };
  return { kind: 'none', summarizable: aggregatesOnServer(component.type) };
}
